// Shared pure helpers for the My Services page, its job list, and job detail
// drawer. Keeping these here lets all three stay in sync with a single
// implementation instead of drifting copies.
import { getRoomRestrictionColor } from "../../utils/roomRestriction";

// Roles that get the "internal" (more detailed) view instead of the
// technician-facing simplified view.
export const INTERNAL_ROLES = ["admin", "user"];

export const toText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

export const getStatusColor = (status) => {
  switch (toText(status)) {
    case "pending":
      return "warning";
    case "assigned":
      return "info";
    case "in_progress":
    case "in progress":
      return "primary";
    case "completed":
      return "success";
    case "failed":
    case "cancelled":
      return "error";
    case "not_applicable":
    case "not applicable":
      return "default";
    default:
      return "default";
  }
};

export const toDateBucket = (dateString) => {
  if (!dateString) return "other";

  const value = new Date(dateString);
  if (Number.isNaN(value.getTime())) return "other";

  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  const key = value.toDateString();
  if (key === yesterday.toDateString()) return "yesterday";
  if (key === today.toDateString()) return "today";
  if (key === tomorrow.toDateString()) return "tomorrow";
  return "other";
};

export const getUserIdCandidates = (user) =>
  [user?.pk_id, user?.id, user?.userId]
    .filter((value) => value !== null && value !== undefined && value !== "")
    .map((value) => String(value));

export const normalizePriority = (priority) =>
  String(priority || "")
    .trim()
    .toLowerCase();

export const isHighPriority = (priority) =>
  normalizePriority(priority) === "high" ||
  normalizePriority(priority) === "urgent";

export const isCurrentUserJob = (job, user, role) => {
  const userIds = getUserIdCandidates(user);
  const assignedId = String(job?.assigned_technician_id || "");
  const assignedName = toText(job?.assigned_technician_name);
  const username = toText(user?.username || user?.uk_username || user?.email);

  if (assignedId && userIds.includes(assignedId)) {
    return true;
  }

  if (assignedName && username && assignedName === username) {
    return true;
  }

  return false;
};

// Hide jobs whose route/assignment period hasn't been finalized or was cancelled.
export const isVisibleAssignmentPeriodStatus = (job) => {
  const status = toText(job?.assignment_period_status);
  return status !== "draft" && status !== "cancelled";
};

export const normalizeTaskStatus = (status) => {
  const normalized = toText(status);
  if (!normalized) return "pending";
  if (normalized === "in progress") return "in_progress";
  return normalized;
};

export const getDefaultTaskStatusFilter = (jobStatus) => {
  const normalized = normalizeTaskStatus(jobStatus);
  return [
    "completed",
    "failed",
    "cancelled",
    "skipped",
    "not_applicable",
  ].includes(normalized)
    ? "finished"
    : "todo";
};

export const resolveNextJobStatus = (currentStatus, allDone) => {
  const normalizedCurrentStatus = normalizeTaskStatus(currentStatus);
  if (
    ["completed", "failed", "cancelled", "skipped", "not_applicable"].includes(
      normalizedCurrentStatus,
    )
  ) {
    return normalizedCurrentStatus;
  }

  if (allDone) {
    return "completed";
  }

  return "in_progress";
};

export const isTerminalTaskStatus = (status) => {
  const normalized = normalizeTaskStatus(status);
  return [
    "completed",
    "skipped",
    "cancelled",
    "failed",
    "not_applicable",
  ].includes(normalized);
};

export const isTerminalJobStatus = (status) => {
  const normalized = normalizeTaskStatus(status);
  return [
    "completed",
    "failed",
    "cancelled",
    "skipped",
    "not_applicable",
  ].includes(normalized);
};

export const getTaskTitle = (task, index, language) => {
  if (language === "da" && task?.task_name_dk) return task.task_name_dk;
  return (
    task?.title || task?.task_name || task?.task_label || `Task ${index + 1}`
  );
};

export const getTaskStableKey = (task, index) =>
  String(
    task?.service_task_id ||
      task?.pk_id ||
      `${task?.task_id || "task"}_${index}`,
  );

export const formatTaskStatusLabel = (status) =>
  normalizeTaskStatus(status).replaceAll("_", " ");

export const shouldPromptFailureReason = (status) =>
  ["failed", "skipped", "not_applicable"].includes(normalizeTaskStatus(status));

export const isNonRoutineTask = (task) => {
  const value = task?.is_non_routine_task;
  return value === true || value === 1 || value === "1";
};

export const getRoomNameColor = (restriction) =>
  getRoomRestrictionColor(restriction);

// MySQL DATETIME columns are returned without a timezone indicator.
// Append 'Z' so the browser treats them as UTC instead of local time.
export const toUtcDate = (value) => {
  if (!value) return null;
  const str = String(value).trim();
  if (!str) return null;
  const hasOffset = /[Zz]|[+-]\d{2}:?\d{2}$/.test(str);
  const normalized = hasOffset ? str : str.replace(" ", "T") + "Z";
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatDateTime = (value) => {
  const d = toUtcDate(value);
  if (!d) return "-";
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const toDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// My Services only needs yesterday/today/tomorrow's jobs.
export const getMyServicesFetchWindow = () => {
  const today = new Date();
  const start = new Date(today);
  start.setDate(start.getDate() - 1);
  const end = new Date(today);
  end.setDate(end.getDate() + 1);

  return { startDate: toDateKey(start), endDate: toDateKey(end) };
};

export const formatTimeLabel = (value) => {
  const d = toUtcDate(value);
  if (!d) return "-";
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  if (sameDay) {
    return d.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatDuration = (minutes) => {
  if (minutes == null || minutes < 0) return null;
  if (minutes === 0) return "< 1 min";
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
};

export const formatPartsUsedForInput = (partsUsed) => {
  if (!partsUsed) return "";

  if (Array.isArray(partsUsed)) {
    return partsUsed.join("\n");
  }

  if (typeof partsUsed === "string") {
    try {
      const parsed = JSON.parse(partsUsed);
      if (Array.isArray(parsed)) {
        return parsed.join("\n");
      }
      return String(partsUsed);
    } catch (_) {
      return String(partsUsed);
    }
  }

  return "";
};

export const parsePartsUsedInput = (partsUsedText) => {
  const cleaned = String(partsUsedText || "").trim();
  if (!cleaned) return null;

  const parts = cleaned
    .split(/\r?\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);

  return parts.length > 0 ? parts : null;
};

export const calculateTimeSpentMinutes = (startedAt, completedAt) => {
  if (!startedAt || !completedAt) return null;

  const started = new Date(startedAt);
  const completed = new Date(completedAt);
  if (Number.isNaN(started.getTime()) || Number.isNaN(completed.getTime())) {
    return null;
  }

  const diffMs = completed.getTime() - started.getTime();
  if (diffMs < 0) return null;
  return Math.round(diffMs / 60000);
};

export const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Set to false to hide the per-task started/completed time chips
export const SHOW_TASK_TIME_CHIPS = false;
export const AUTO_FAIL_PENDING_TASK_REASON = "Failed because job was failed";

// ── Job ordering (service assistant) ─────────────────────────────────────────

const toOrderNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

// urgent first, then high, then everything else.
export const getPriorityRank = (priority) => {
  const normalized = normalizePriority(priority);
  if (normalized === "urgent") return 0;
  if (normalized === "high") return 1;
  return 2;
};

const compareKeys = (a, b) => {
  for (let index = 0; index < Math.max(a.length, b.length); index += 1) {
    const x = a[index] ?? 0;
    const y = b[index] ?? 0;
    if (x < y) return -1;
    if (x > y) return 1;
  }
  return 0;
};

/**
 * Default (route) order for one day's jobs:
 *   1. finished jobs last;
 *   2. urgent, then high priority, ahead of everything else;
 *   3. jobs in assignment periods, in route order:
 *      - one period: the period's execution order;
 *      - several periods from the same route template that are not merged:
 *        the template's master order (global_order) across them, when every
 *        job has one; otherwise period by period (by period start), each in
 *        its execution order;
 *   4. jobs without a period after those, by scheduled time.
 * Uses sort keys rather than a pairwise rule so the order is always consistent.
 */
export const orderJobsByRoute = (jobs = []) => {
  const periodJobs = jobs.filter((job) => job?.assignment_period_id);
  const periodIds = new Set(periodJobs.map((job) => String(job.assignment_period_id)));
  const templateIds = new Set(
    periodJobs.map((job) => String(job.assignment_route_template_id ?? "")),
  );
  const useMasterOrder =
    periodIds.size > 1 &&
    templateIds.size === 1 &&
    !templateIds.has("") &&
    periodJobs.every((job) => toOrderNumber(job.master_order) !== null);

  const keyOf = (job) => {
    const terminal = isTerminalJobStatus(job?.status) ? 1 : 0;
    const priority = getPriorityRank(job?.priority);
    const id = Number(job?.pk_id) || 0;

    if (!job?.assignment_period_id) {
      const time = toUtcDate(job?.scheduled_start || job?.created_at)?.getTime() || 0;
      return [terminal, priority, 1, time, 0, 0, id];
    }

    const periodStart = toUtcDate(job.assignment_period_start)?.getTime() || 0;
    const execution = toOrderNumber(job.assignment_execution_order) ?? Number.MAX_SAFE_INTEGER;
    if (useMasterOrder) {
      return [terminal, priority, 0, toOrderNumber(job.master_order), periodStart, execution, id];
    }
    return [
      terminal,
      priority,
      0,
      periodStart,
      Number(job.assignment_period_id),
      execution,
      id,
    ];
  };

  return jobs
    .map((job) => ({ job, key: keyOf(job) }))
    .sort((a, b) => compareKeys(a.key, b.key))
    .map((entry) => entry.job);
};

// ── Manual job order cache ──────────────────────────────────────────────────
// Stored per user and per calendar date (not "today"/"tomorrow"), together
// with the route order it was made against. If the office later changes the
// route order, the manual order for that day is dropped instead of silently
// overriding the new route. Dates before yesterday are pruned.

export const JOB_ORDER_STORAGE_KEY = "myServices.jobOrder.v2";
const LEGACY_JOB_ORDER_STORAGE_KEY = "myServices.jobOrder";

export const getJobOrderUserKey = (user) =>
  String(user?.pk_id ?? user?.userId ?? user?.id ?? user?.username ?? user?.email ?? "anonymous");

export const dateKeyForDayTab = (dayTab, now = new Date()) => {
  const date = new Date(now);
  if (dayTab === "yesterday") date.setDate(date.getDate() - 1);
  if (dayTab === "tomorrow") date.setDate(date.getDate() + 1);
  return toDateKey(date);
};

export const loadJobOrderStore = () => {
  try {
    window.localStorage.removeItem(LEGACY_JOB_ORDER_STORAGE_KEY);
    const raw = window.localStorage.getItem(JOB_ORDER_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return pruneJobOrderStore(parsed && typeof parsed === "object" ? parsed : {});
  } catch {
    return {};
  }
};

export const saveJobOrderStore = (store) => {
  try {
    window.localStorage.setItem(JOB_ORDER_STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Storage unavailable (private mode / full): order just isn't remembered.
  }
};

export const pruneJobOrderStore = (store, now = new Date()) => {
  const oldestKept = dateKeyForDayTab("yesterday", now);
  const next = {};
  Object.entries(store || {}).forEach(([userKey, days]) => {
    const keptDays = {};
    Object.entries(days || {}).forEach(([dateKey, entry]) => {
      if (dateKey >= oldestKept && Array.isArray(entry?.order)) {
        keptDays[dateKey] = entry;
      }
    });
    if (Object.keys(keptDays).length > 0) next[userKey] = keptDays;
  });
  return next;
};

/**
 * Apply a saved manual order to the route-ordered jobs of one day.
 * Returns { jobs, stale }. stale = the route order of the saved jobs changed
 * since the manual order was made, so the manual order is ignored.
 * Jobs that were not there when the order was saved are slotted in after the
 * job that precedes them in the route order (so a new urgent job still ends
 * up at the top).
 */
export const applySavedJobOrder = (routeOrderedJobs, saved) => {
  if (!saved || !Array.isArray(saved.order) || saved.order.length === 0) {
    return { jobs: routeOrderedJobs, stale: false };
  }

  const routeIds = routeOrderedJobs.map((job) => String(job.pk_id));
  const baseline = Array.isArray(saved.baseline) ? saved.baseline.map(String) : [];
  if (baseline.length > 0) {
    const routeIdSet = new Set(routeIds);
    const baselineSet = new Set(baseline);
    const baselineStillHere = baseline.filter((id) => routeIdSet.has(id));
    const routeOfBaseline = routeIds.filter((id) => baselineSet.has(id));
    if (baselineStillHere.join(",") !== routeOfBaseline.join(",")) {
      return { jobs: routeOrderedJobs, stale: true };
    }
  }

  const byId = new Map(routeOrderedJobs.map((job) => [String(job.pk_id), job]));
  const ordered = saved.order.map(String).filter((id) => byId.has(id));
  const placed = new Set(ordered);

  routeIds.forEach((id, index) => {
    if (placed.has(id)) return;
    let insertAt = 0;
    for (let back = index - 1; back >= 0; back -= 1) {
      const position = ordered.indexOf(routeIds[back]);
      if (position !== -1) {
        insertAt = position + 1;
        break;
      }
    }
    ordered.splice(insertAt, 0, id);
    placed.add(id);
  });

  return { jobs: ordered.map((id) => byId.get(id)), stale: false };
};
